import { Controller } from "@hotwired/stimulus";
import { createPopper } from "@popperjs/core";
import { getParameterOverrides } from "hb/insights/query_parameters";

// Precedence: required > override > lit > dormant.
export function computeState(queries) {
  const params = getParameterOverrides(queries);
  if (params.length === 0) return "dormant";
  if (params.some((p) => p.isRequired)) return "required";
  if (params.some((p) => p.hasOverride)) return "override";
  return "lit";
}

export default class extends Controller {
  static targets = [
    "button",
    "popover",
    "list",
    "empty",
    "dot",
    "resetBtn",
    "footer",
  ];
  static values = { scope: { type: String, default: "dashboard" } };

  connect() {
    this.updateState();
    this.urlChangedHandler = () => this.updateState();
    window.addEventListener("urlchanged", this.urlChangedHandler);

    this.outsideClickHandler = (event) => {
      if (this.popoverTarget.hidden) return;
      if (this.element.contains(event.target)) return;
      this.close();
    };
    document.addEventListener("click", this.outsideClickHandler);

    this.escapeKeyHandler = (event) => {
      if (event.key !== "Escape") return;
      if (this.popoverTarget.hidden) return;
      this.close();
    };
    document.addEventListener("keydown", this.escapeKeyHandler);

    this.openRequestHandler = (event) => {
      const requestedScope = event?.detail?.scope;
      if (requestedScope && requestedScope !== this.scopeValue) return;
      setTimeout(() => this.open(), 0);
    };
    window.addEventListener("parametersPopover:open", this.openRequestHandler);

    // dashboard:addWidget fires before the new widget has a grid-item
    // controller attached, so it's a no-op here — the MutationObserver below
    // picks up the insertion. We only listen for query edits on existing
    // widgets (which don't mutate the DOM tree the observer watches).
    this.widgetChangeHandler = () => this.updateState();
    window.addEventListener(
      "dashboard:widgetQueryChanged",
      this.widgetChangeHandler,
    );

    if (this.scopeValue === "query") {
      this.queryInputHandler = (event) => {
        const target = event.target;
        if (
          target?.id === "query" &&
          target.closest('[data-controller*="insights--query-component"]')
        ) {
          this.updateState();
        }
      };
      document.addEventListener("input", this.queryInputHandler);
    }

    if (this.scopeValue === "dashboard") {
      this.widgetObserver = new MutationObserver((mutations) => {
        for (const m of mutations) {
          const nodes = [...m.addedNodes, ...m.removedNodes];
          if (
            nodes.some(
              (n) =>
                n.nodeType === 1 &&
                (n.matches?.(
                  '[data-controller*="insights--dashboard--grid-item"]',
                ) ||
                  n.querySelector?.(
                    '[data-controller*="insights--dashboard--grid-item"]',
                  )),
            )
          ) {
            this.updateState();
            return;
          }
        }
      });
      const root = document.querySelector(".grid-stack") || document.body;
      this.widgetObserver.observe(root, { childList: true, subtree: true });
    }

    this.popper = createPopper(this.buttonTarget, this.popoverTarget, {
      placement: "bottom-end",
      // `fixed` so the popover escapes the query card's `overflow: hidden`.
      strategy: "fixed",
      modifiers: [
        { name: "offset", options: { offset: [0, 6] } },
        { name: "flip", options: { rootBoundary: "viewport" } },
        {
          name: "preventOverflow",
          options: { padding: 8, rootBoundary: "viewport" },
        },
      ],
    });
  }

  disconnect() {
    window.removeEventListener("urlchanged", this.urlChangedHandler);
    document.removeEventListener("click", this.outsideClickHandler);
    document.removeEventListener("keydown", this.escapeKeyHandler);
    window.removeEventListener(
      "parametersPopover:open",
      this.openRequestHandler,
    );
    window.removeEventListener(
      "dashboard:widgetQueryChanged",
      this.widgetChangeHandler,
    );
    if (this.queryInputHandler) {
      document.removeEventListener("input", this.queryInputHandler);
    }
    if (this.widgetObserver) this.widgetObserver.disconnect();
    this.popper?.destroy();
  }

  collectQueries() {
    if (this.scopeValue === "query") {
      const textarea = document.querySelector(
        '[data-controller*="insights--query-component"] textarea#query',
      );
      return textarea ? [textarea.value || ""] : [];
    }
    // dashboard scope
    const widgets = document.querySelectorAll(
      '[data-controller*="insights--dashboard--grid-item"]',
    );
    const queries = Array.from(widgets).map(
      (w) =>
        w.dataset.insightsDashboardGridItemQueryValue || w.dataset.query || "",
    );
    // Include the query being edited in the config panel (not yet saved to the widget)
    const editingConfig = document.querySelector(
      '[data-controller*="insights--widgets--insights-vis-config-component"][data-query]',
    );
    if (editingConfig?.dataset.query) {
      queries.push(editingConfig.dataset.query);
    }
    return queries;
  }

  updateState() {
    const state = computeState(this.collectQueries());
    this.buttonTarget.dataset.state = state;
    if (this.hasDotTarget) {
      this.dotTarget.hidden = state !== "override" && state !== "required";
      this.dotTarget.dataset.tone =
        state === "required" ? "required" : "override";
    }
    if (this.hasResetBtnTarget) {
      this.resetBtnTarget.disabled = !this.hasAnyOverride();
    }
  }

  hasAnyOverride() {
    return getParameterOverrides(this.collectQueries()).some(
      (p) => p.hasOverride,
    );
  }

  toggle(event) {
    event.stopPropagation();
    if (this.popoverTarget.hidden) {
      this.open();
    } else {
      this.close();
    }
  }

  open() {
    this.populate();
    this.popoverTarget.hidden = false;
    this.buttonTarget.setAttribute("aria-expanded", "true");
    // Re-measure now that the popover is laid out, so placement flips/shifts
    // if the anchor is near a viewport edge.
    this.popper?.update();
  }

  close() {
    this.popoverTarget.hidden = true;
    this.buttonTarget.setAttribute("aria-expanded", "false");
  }

  populate() {
    const params = getParameterOverrides(this.collectQueries());
    if (params.length === 0) {
      this.listTarget.innerHTML = "";
      if (this.hasEmptyTarget) this.emptyTarget.hidden = false;
      if (this.hasFooterTarget) this.footerTarget.hidden = true;
      return;
    }
    if (this.hasEmptyTarget) this.emptyTarget.hidden = true;
    if (this.hasFooterTarget) this.footerTarget.hidden = false;
    this.listTarget.innerHTML = params
      .map((p) => {
        const placeholder = this.placeholderFor(p);
        return `
          <div class="parameters-popover-field">
            <label>${this.escapeHtml(p.name)}</label>
            <input type="text"
                   data-param-name="${this.escapeHtml(p.name)}"
                   value="${p.hasOverride ? this.escapeHtml(p.urlValue) : ""}"
                   placeholder="${this.escapeHtml(placeholder)}">
          </div>
        `;
      })
      .join("");
  }

  placeholderFor(param) {
    if (param.defaultState === "consistent") return param.value || "";
    if (param.defaultState === "varies") return "Varies by widget";
    return "(required)";
  }

  apply() {
    const url = new URL(window.location);
    const inputs = this.listTarget.querySelectorAll("input[data-param-name]");
    let didChange = false;
    inputs.forEach((input) => {
      const paramName = input.dataset.paramName;
      const value = input.value.trim();
      const existing = url.searchParams.get(paramName) || "";
      if (value === existing) return;
      if (value) {
        url.searchParams.set(paramName, value);
      } else {
        url.searchParams.delete(paramName);
      }
      didChange = true;
    });
    if (didChange) {
      history.replaceState({}, "", url.toString());
      window.dispatchEvent(new CustomEvent("urlchanged"));
    }
    this.close();
  }

  reset() {
    const url = new URL(window.location);
    const params = getParameterOverrides(this.collectQueries());
    let didChange = false;
    params.forEach((p) => {
      if (url.searchParams.has(p.name)) {
        url.searchParams.delete(p.name);
        didChange = true;
      }
    });
    if (didChange) {
      history.replaceState({}, "", url.toString());
      window.dispatchEvent(new CustomEvent("urlchanged"));
    }
    this.close();
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text == null ? "" : String(text);
    return div.innerHTML;
  }
}
